import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Link } from "react-router";
import { ArrowRight, Download, ExternalLink, Package, RefreshCw, Search } from "lucide-react";
import { SkeletonCard } from "@/components/skeleton";
import { formatCurrency as formatMoney, formatDate as formatSharedDate, formatNumber, formatPercent } from "@/lib/i18n";

interface MarketplaceKit {
  id: string;
  name: string;
  description: string;
  assets: Array<{ type: string; name: string }>;
  installCount: number;
  version: string;
  status: "draft" | "published";
  createdAt: string | null;
  ownershipLabel: string;
  updateLabel: string;
  flowStatus: "marketplace" | "legacy_bridge";
  installState: {
    status: "preview_only" | "checkout_required" | "owned_by_creator" | "already_purchased" | "already_deployed" | "install_available";
    label: string;
    description: string;
    canInstall: boolean;
  };
  previewState: {
    status: "available" | "unavailable";
    label: string;
    description: string;
  };
  performancePreview: {
    avgConversionRatePercent: number | null;
    avgEmailOpenRatePercent: number | null;
    avgRoas: number | null;
    sampleSize: number;
    isVerified: boolean;
  } | null;
}

interface CreatorPackage {
  id: string;
  name: string;
  status: "draft" | "review" | "published" | "rejected" | "archived" | "suspended";
  version: string;
  installs: number;
  revenue: number;
  reviewCount: number;
  reviewPipeline: {
    pendingCount: number;
    lastPendingSubmittedAt: string | null;
  };
  installAdoption: {
    deployedCount: number;
    pendingSetupCount: number;
    lastPurchasedAt: string | null;
    lastDeployedAt: string | null;
  };
  lifecycle: {
    label: string;
    description: string;
    availableActions: Array<"submit_for_review" | "withdraw_to_draft" | "archive_listing">;
  };
  listingPath: string | null;
}

interface CreatorDashboardData {
  packages: CreatorPackage[];
  publishingSurface: {
    status: "submission_mounted";
    label: string;
    description: string;
  };
}

interface MarketplacePackageCard {
  id: string;
  title: string;
  description: string | null;
  packageSummary?: {
    assetCounts?: Record<string, number>;
  };
  installCount: number;
  snapshotVersionId?: string;
  publishedAt?: string | null;
  previewState?: {
    status: "available" | "unavailable";
    label: string;
    description: string;
  };
  performancePreview?: {
    avgConversionRatePercent: number | null;
    avgEmailOpenRatePercent: number | null;
    avgRoas: number | null;
    sampleSize: number;
    isVerified: boolean;
  } | null;
  installState?: {
    status: "preview_only" | "checkout_required" | "owned_by_creator" | "already_purchased" | "already_deployed" | "install_available";
    label: string;
    description: string;
    canInstall: boolean;
  };
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return formatSharedDate(parsed);
}

function formatWholeCurrency(value: number): string {
  return formatMoney(value * 100, "EUR", { maximumFractionDigits: 0 });
}

function mapMarketplacePackageToKit(pkg: MarketplacePackageCard): MarketplaceKit {
  const assetEntries = Object.entries(pkg.packageSummary?.assetCounts ?? {});
  const installState = pkg.installState ?? {
    status: "preview_only",
    label: "Preview only",
    description: "The mounted marketplace route only supports package review for this listing right now.",
    canInstall: false,
  };
  const previewState = pkg.previewState ?? {
    status: "unavailable",
    label: "Preview unavailable",
    description: "The mounted marketplace route does not currently expose a preview-ready package snapshot for this listing.",
  };

  return {
    id: pkg.id,
    name: pkg.title,
    description: pkg.description ?? "Marketplace package",
    assets: assetEntries.map(([type, count]) => ({ type, name: `${count} assets` })),
    installCount: pkg.installCount ?? 0,
    version: pkg.snapshotVersionId ?? "published",
    status: "published",
    createdAt: pkg.publishedAt ?? null,
    ownershipLabel: "Install creates buyer-owned copies",
    updateLabel: "Creator updates publish a newer marketplace version offer",
    flowStatus: "marketplace",
    installState,
    previewState,
    performancePreview: pkg.performancePreview ?? null,
  };
}

function formatPerformancePreview(
  performancePreview: MarketplaceKit["performancePreview"],
): { summary: string | null; sampleLabel: string | null } {
  if (!performancePreview?.isVerified) {
    return { summary: null, sampleLabel: null };
  }

  const parts = [
    performancePreview.avgConversionRatePercent !== null
      ? `${formatPercent(performancePreview.avgConversionRatePercent)} conv`
      : null,
    performancePreview.avgEmailOpenRatePercent !== null
      ? `${formatPercent(performancePreview.avgEmailOpenRatePercent)} opens`
      : null,
    performancePreview.avgRoas !== null
      ? `${formatNumber(performancePreview.avgRoas)}x ROAS`
      : null,
  ].filter((value): value is string => Boolean(value));

  return {
    summary: parts.length > 0 ? parts.join(" · ") : null,
    sampleLabel: `${formatNumber(performancePreview.sampleSize)} verified install${performancePreview.sampleSize === 1 ? "" : "s"}`,
  };
}

export default function KitBuilderPage() {
  const [kits, setKits] = useState<MarketplaceKit[]>([]);
  const [creatorDashboard, setCreatorDashboard] = useState<CreatorDashboardData | null>(null);
  const [isLoadingMarketplace, setIsLoadingMarketplace] = useState(true);
  const [isLoadingCreator, setIsLoadingCreator] = useState(true);
  const [view, setView] = useState<"creator" | "marketplace">("marketplace");
  const [search, setSearch] = useState("");
  const [installingId, setInstallingId] = useState<string | null>(null);
  const [lifecycleBusyId, setLifecycleBusyId] = useState<string | null>(null);

  const fetchKits = useCallback(async () => {
    setIsLoadingMarketplace(true);
    try {
      const params = new URLSearchParams({ limit: "24" });
      if (search) params.set("q", search);
      const res = await apiFetch<{ data: MarketplacePackageCard[] }>(`/v1/marketplace/packages?${params.toString()}`);
      setKits((res.data ?? []).map(mapMarketplacePackageToKit));
    } catch {
      setKits([]);
    } finally {
      setIsLoadingMarketplace(false);
    }
  }, [search]);

  const fetchCreatorDashboard = useCallback(async () => {
    setIsLoadingCreator(true);
    try {
      const res = await apiFetch<{ data: CreatorDashboardData }>("/v1/marketplace/creator/dashboard");
      setCreatorDashboard(res.data);
    } catch {
      setCreatorDashboard(null);
    } finally {
      setIsLoadingCreator(false);
    }
  }, []);

  useEffect(() => { void fetchKits(); }, [fetchKits]);
  useEffect(() => { void fetchCreatorDashboard(); }, [fetchCreatorDashboard]);

  const handleInstall = async (id: string) => {
    setInstallingId(id);
    try {
      await apiFetch(`/v1/marketplace/packages/${id}/install`, {
        method: "POST",
        body: JSON.stringify({
          stripePaymentMethodId: null,
          excludeAssetIds: [],
        }),
      });
      await fetchKits();
    } catch {
      // Surface remains intentionally quiet until the workspace gets shared marketplace toasts.
    } finally {
      setInstallingId(null);
    }
  };

  const handleLifecycleAction = async (
    id: string,
    action: "submit_for_review" | "withdraw_to_draft" | "archive_listing",
  ) => {
    setLifecycleBusyId(id);
    try {
      await apiFetch(`/v1/marketplace/creator/packages/${id}/lifecycle`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
      await fetchCreatorDashboard();
    } catch {
      // Keep this bridge page truthful but minimal.
    } finally {
      setLifecycleBusyId(null);
    }
  };

  const isLoading = isLoadingMarketplace || (view === "creator" && isLoadingCreator);
  const creatorPackages = creatorDashboard?.packages ?? [];
  const isMarketplaceEmpty = kits.length === 0;
  const isCreatorEmpty = creatorPackages.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3"><Package className="h-5 w-5 text-[var(--color-primary)]" /><h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Asset Kits</h1></div>
        <div className="flex gap-2">
          <div className="flex rounded-lg bg-[var(--color-surface)] p-0.5">
            {(["marketplace", "creator"] as const).map(v => <button key={v} type="button" onClick={() => setView(v)} className={`rounded-md px-3 py-1 text-xs font-medium capitalize ${view === v ? "bg-[var(--color-background)] text-[var(--color-foreground)] shadow-sm" : "text-[var(--color-muted-foreground)]"}`}>{v === "creator" ? "Creator Queue" : "Marketplace"}</button>)}
          </div>
          <Link
            to="/marketplace/creator"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-sm text-white hover:opacity-90"
          >
            Open Creator Dashboard
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
        <p className="font-medium text-[var(--color-foreground)]">Mounted marketplace is the canonical kit system.</p>
        <p className="mt-1">Installing a package creates buyer-owned copies. Creator updates publish a newer snapshot version offer, and existing buyer installs are never auto-overwritten. This workspace now mirrors those mounted marketplace semantics instead of the retired legacy `/affiliate/kits*` bridge.</p>
      </div>

      {view === "marketplace" && (
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-muted-foreground)]" /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search kits..." className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] pl-9 pr-3 py-2 text-sm text-[var(--color-foreground)]" /></div>
      )}

      {isLoading ? <div className="grid grid-cols-2 gap-4">{[1,2,3,4].map(i=><SkeletonCard key={i} className="h-48 rounded-xl" />)}</div> : (
        view === "marketplace" ? (
          isMarketplaceEmpty ? (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center"><Package className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" /><p className="font-medium text-[var(--color-foreground)]">No kits found</p></div>
          ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {kits.map(kit => (
              <div key={kit.id} className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                {(() => {
                  const { summary, sampleLabel } = formatPerformancePreview(kit.performancePreview);
                  return (
                    <>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-[var(--color-foreground)]">{kit.name}</h3>
                  <span className="text-xs text-[var(--color-muted-foreground)]">v{kit.version}</span>
                </div>
                <p className="text-sm text-[var(--color-muted-foreground)] mb-3 line-clamp-2">{kit.description}</p>
                <div className="flex flex-wrap gap-1 mb-3">{kit.assets.map((a, i) => <span key={i} className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)]">{a.type}: {a.name}</span>)}</div>
                <div className="mb-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-medium text-[var(--color-foreground)]">{kit.installState.label}</p>
                    {kit.createdAt ? (
                      <p className="text-[11px] text-[var(--color-muted-foreground)]">Published {formatDate(kit.createdAt)}</p>
                    ) : null}
                  </div>
                  <p className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">{kit.installState.description}</p>
                </div>
                <div className="mb-3 space-y-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-[11px] text-[var(--color-muted-foreground)]">
                  <p className="font-medium text-[var(--color-foreground)]">{kit.previewState.label}</p>
                  <p>{kit.previewState.description}</p>
                  <p>
                    {summary
                      ? `Verified performance: ${summary}`
                      : "No verified conversion, email, or ROAS preview is mounted for this package yet."}
                  </p>
                  {sampleLabel ? <p>{sampleLabel}</p> : null}
                </div>
                <div className="mb-3 space-y-1 rounded-lg bg-[var(--color-surface)] p-2 text-[11px] text-[var(--color-muted-foreground)]">
                  <p>{kit.ownershipLabel}</p>
                  <p>{kit.updateLabel}</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[var(--color-muted-foreground)]">{formatNumber(kit.installCount)} installs</span>
                  {kit.installState.canInstall ? (
                    <button type="button" onClick={() => handleInstall(kit.id)} disabled={installingId === kit.id} className="rounded-lg bg-[var(--color-primary)] px-3 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50">
                      {installingId === kit.id ? "Installing..." : <><Download className="inline h-3 w-3 mr-1" />Install</>}
                    </button>
                  ) : (
                    <Link
                      to={`/marketplace/packages/${kit.id}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-foreground)]"
                    >
                      {kit.installState.status === "already_purchased"
                        ? "Resume setup"
                        : kit.installState.status === "already_deployed"
                          ? "View state"
                          : kit.installState.status === "checkout_required"
                            ? "Review & checkout"
                            : "Review package"}
                    </Link>
                  )}
                </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
          )
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 text-sm text-[var(--color-muted-foreground)]">
              <p className="font-medium text-[var(--color-foreground)]">{creatorDashboard?.publishingSurface.label ?? "Creator submission workflow"}</p>
              <p className="mt-1">{creatorDashboard?.publishingSurface.description ?? "The mounted marketplace creator dashboard owns package submission, archive, and draft withdrawal."}</p>
            </div>
            {isCreatorEmpty ? (
              <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center"><Package className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" /><p className="font-medium text-[var(--color-foreground)]">No creator packages yet</p><p className="mt-2 text-sm text-[var(--color-muted-foreground)]">Use the mounted creator dashboard workflow to prepare a package for marketplace review.</p></div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {creatorPackages.map((pkg) => {
                  const primaryAction = pkg.lifecycle.availableActions[0] ?? null;
                  return (
                    <div key={pkg.id} className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <h3 className="font-medium text-[var(--color-foreground)]">{pkg.name}</h3>
                          <p className="text-xs text-[var(--color-muted-foreground)]">v{pkg.version} · {pkg.status}</p>
                        </div>
                        {pkg.listingPath ? (
                          <Link to={pkg.listingPath} className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)]">
                            Listing
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : null}
                      </div>
                      <div className="mt-3 rounded-lg bg-[var(--color-surface)] p-3 text-xs text-[var(--color-muted-foreground)]">
                        <p>{pkg.lifecycle.description}</p>
                      </div>
                      <div className="mt-3 grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-2">
                        <div className="rounded-lg bg-[var(--color-surface)] p-3">
                          <p className="font-medium text-[var(--color-foreground)]">Review pipeline</p>
                          <p className="mt-1">
                            {pkg.reviewPipeline.pendingCount > 0
                              ? `${formatNumber(pkg.reviewPipeline.pendingCount)} buyer review${pkg.reviewPipeline.pendingCount === 1 ? "" : "s"} pending moderation`
                              : "No buyer reviews waiting on moderation"}
                          </p>
                          {pkg.reviewPipeline.lastPendingSubmittedAt ? (
                            <p className="mt-1">Last pending review {formatDate(pkg.reviewPipeline.lastPendingSubmittedAt)}</p>
                          ) : null}
                        </div>
                        <div className="rounded-lg bg-[var(--color-surface)] p-3">
                          <p className="font-medium text-[var(--color-foreground)]">Install adoption</p>
                          <p className="mt-1">{formatNumber(pkg.installAdoption.deployedCount)} deployed · {formatNumber(pkg.installAdoption.pendingSetupCount)} awaiting setup</p>
                          {pkg.installAdoption.lastPurchasedAt ? (
                            <p className="mt-1">Last purchased {formatDate(pkg.installAdoption.lastPurchasedAt)}</p>
                          ) : null}
                          {pkg.installAdoption.lastDeployedAt ? (
                            <p className="mt-1">Last deployed {formatDate(pkg.installAdoption.lastDeployedAt)}</p>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                        <span>{formatNumber(pkg.installs)} installs</span>
                        <span>{formatNumber(pkg.reviewCount)} reviews</span>
                        <span>{formatWholeCurrency(pkg.revenue)}</span>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <Link to="/marketplace/creator" className="text-xs text-[var(--color-muted-foreground)] underline-offset-2 hover:underline">
                          Open full creator workflow
                        </Link>
                        {primaryAction ? (
                          <button
                            type="button"
                            onClick={() => handleLifecycleAction(pkg.id, primaryAction)}
                            disabled={lifecycleBusyId === pkg.id}
                            className="rounded-lg border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-foreground)] disabled:opacity-50"
                          >
                            {lifecycleBusyId === pkg.id ? (
                              "Updating..."
                            ) : (
                              <>
                                <RefreshCw className="mr-1 inline h-3 w-3" />
                                {primaryAction === "submit_for_review"
                                  ? "Submit for review"
                                  : primaryAction === "withdraw_to_draft"
                                  ? "Withdraw to draft"
                                  : "Archive listing"}
                              </>
                            )}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
